<script setup lang="ts">
import PdsButton from '@/components/pds/PdsButton.vue';
import PdsModal from '@/components/pds/PdsModal.vue';
import { computed, ref } from 'vue';
import { useOperator } from '../composables/useOperator';
import { formatDate, formatTime } from '../format';
import { stockToReceipt } from '../print/printer.receipts';
import { usePrinter } from '../print/usePrinter';
import type { StockLine } from '../stock';
import PaperTicket from './PaperTicket.vue';
import PrinterStatus from './PrinterStatus.vue';
import { usePdsOrdersStore } from '../stores/orders.store';

interface PrintZone {
  name: string;
  since?: string;
  lines: StockLine[];
}

const props = defineProps<{ zones: PrintZone[]; done: StockLine[] }>();
defineEmits<{ close: [] }>();

const { name, store } = useOperator();
const orders = usePdsOrdersStore();

// La liste est figée à l'ouverture : ce qui s'imprime est ce que l'aperçu montre.
const stoppedAt = orders.now;
const time = formatTime(stoppedAt);
const printer = usePrinter();
const sent = ref(false);

const print = async () => {
  sent.value = false;
  sent.value = await printer.print(
    stockToReceipt(props.zones, props.done, store.value, stoppedAt, name.value),
  );
};

const lines = computed(() => props.zones.flatMap((zone) => zone.lines));
const pieces = computed(() =>
  lines.value.reduce((sum, line) => sum + line.quantity, 0),
);
const orderCount = computed(
  () => new Set(lines.value.flatMap((line) => line.orders)).size,
);
</script>

<template>
  <PdsModal size="base" @close="$emit('close')">
    <template #title>Imprimer la liste</template>
    <template #subtitle>
      {{ lines.length }} références · {{ pieces }} pièces · liste arrêtée à
      {{ time }}
    </template>

    <div class="flex flex-col gap-[18px] px-5 py-6">
      <p
        class="text-encre-douce text-[12px] leading-[14px] font-semibold tracking-[0.9px] uppercase"
      >
        Aperçu ticket 80 mm, environ 22 cm
      </p>
      <div
        class="bg-creux flex h-80 justify-center overflow-hidden rounded-[24px]"
      >
        <PaperTicket class="h-fit">
          <div class="text-center">
            <p class="font-bold uppercase">{{ store }}</p>
            <p class="text-[18px] leading-[1.2] font-bold uppercase">
              Ce qu’il faut préparer
            </p>
            <p>{{ formatDate(stoppedAt) }} · {{ time }}</p>
          </div>
          <hr class="my-[10px] h-[3px] border-0 bg-[#1a1a1a]" />
          <p class="text-center">
            {{ lines.length }} références · {{ pieces }} pièces ·
            {{ orderCount }} commandes
          </p>

          <template v-for="zone in zones" :key="zone.name">
            <hr class="my-[10px] h-[3px] border-0 bg-[#1a1a1a]" />
            <p class="font-bold uppercase">{{ zone.name }}</p>
            <p v-if="zone.since" class="pb-[10px]">dès {{ zone.since }}</p>
            <div
              v-for="line in zone.lines"
              :key="line.id"
              class="border-t border-[#1a1a1a] py-[10px]"
            >
              <p
                class="flex items-center gap-2 text-[18px] leading-[1.2] font-bold uppercase"
              >
                <span
                  class="size-[14px] shrink-0 border-2 border-[#1a1a1a]"
                ></span>
                {{ line.quantity }} {{ line.name }}
              </p>
              <p class="pl-[22px]">{{ line.dueAt }}</p>
              <p v-if="line.option" class="pl-[22px]">{{ line.option }}</p>
            </div>
          </template>

          <template v-if="done.length">
            <hr class="my-[10px] h-[3px] border-0 bg-[#1a1a1a]" />
            <p class="font-bold uppercase">Déjà préparé — ne pas reprendre</p>
            <p v-for="line in done" :key="line.id" class="pt-[3px]">
              {{ line.quantity }} {{ line.name }} ·
              {{ line.parts.map((part) => `#${part.ref}`).join(' ') }}
            </p>
          </template>

          <hr class="my-[10px] h-[3px] border-0 bg-[#1a1a1a]" />
          <div class="text-center">
            <p>Imprimé à {{ time }} par {{ name }}</p>
            <p>Liste arrêtée à {{ time }} — vérifier le tableau</p>
          </div>
        </PaperTicket>
      </div>

      <PrinterStatus />
      <p
        v-if="sent"
        role="status"
        class="text-succes-texte text-[12px] leading-[17px]"
      >
        Envoyé à l’imprimante.
      </p>

      <p class="text-encre-douce text-[12px] leading-[17px]">
        Liste arrêtée à {{ time }} · vérifier le tableau avant de sortir les
        produits.
      </p>
    </div>

    <template #footer>
      <PdsButton variant="second" fit @click="$emit('close')">Retour</PdsButton>
      <PdsButton
        fit
        class="sm:ml-auto"
        :loading="printer.printing.value"
        :disabled="!printer.configured.value"
        :title="
          printer.configured.value
            ? undefined
            : 'Choisissez une imprimante dans les réglages'
        "
        @click="print"
      >
        Imprimer
      </PdsButton>
    </template>
  </PdsModal>
</template>
